import common from '../common';
import commonGame from '../commonGame';

const _processEvents = Symbol('_processEvents');
const _showHand = Symbol('_showHand');
const _hideHand = Symbol('_hideHand');
const _checkTutorialEnd = Symbol('_checkTutorialEnd');

export default class Tutorial extends PIXI.Container{
	constructor() {
		super();
		// Enumerators.
		this.STATE = {
			DRAW: 0,
			TOOL: 1,
			PALETTE: 2,
			IDLE: 3
		};

		// Constants.
		this.TUT_STEP_DELAY = 3;

		// Attributes.
		this._isPaused = false;
		this._tutorialStepTimer = 0;
		this._tutorialHand = null;
		this._state = this.STATE.IDLE;
		this._arrEvent = [];
		this._arrTutorialStep = [{
			state: this.STATE.DRAW,
			isValid: false,
			isDone: !common.savedData.isDrawTutorial
		}, {
			state: this.STATE.TOOL,
			isValid: true,
			isDone: !common.savedData.isToolTutorial
		}, {
			state: this.STATE.PALETTE,
			isValid: false,
			isDone: !common.savedData.isPaletteTutorial
		}];


		// Signals.
		this.signals = {};
		this.signals.onCompleted = new P3.utils.MiniSignal();

		this._tweens = new P3.utils.TWEEN.Group();
	}

	init() {
		this._tutorialHand = new PIXI.spine.Spine(P3.assets.spineData('tutorial_hand'));
		this._tutorialHand.skeleton.setToSetupPose();
		this._tutorialHand.skeleton.setSkin(null);
		this._tutorialHand.skeleton.setSkinByName('default');
		this._tutorialHand.autoUpdate = false;
		this._tutorialHand.scale = new PIXI.Point(0.7, 0.7);
		this._tutorialHand.alpha = 0;
		this.addChild(this._tutorialHand);

		this._tutorialHand.state.setAnimationByName(0, 'still_' + ( PIXI.utils.isMobile ? 'mobile' : 'desktop' ), true);
		this._tutorialHand.update(0);
	}

	end(){
		this.removeChild(this._tutorialHand);
		this._tutorialHand.destroy();
		this._tutorialHand = null;
	}

	destroy(options) {
		this._tweens.removeAll();
		super.destroy();
	}

	update() {
		this._tweens.update();
		if (this._tutorialHand == null) return;


		this._tutorialHand.update(P3.Ticker.dt);

		this[_processEvents]();

		// Delayed tutorial step activation.
		if (!this._isPaused) {
			this._tutorialStepTimer += P3.Ticker.dt;
			if (this._tutorialStepTimer >= this.TUT_STEP_DELAY && this._state === this.STATE.IDLE) {
				for (const tutorialStep of this._arrTutorialStep) {
					if (tutorialStep.isValid && !tutorialStep.isDone) {
						this._state = tutorialStep.state;
						switch (this._state) {
							case this.STATE.DRAW:
								this._tutorialHand.state.setAnimationByName(0, 'drag_' + (PIXI.utils.isMobile ? 'mobile' : 'desktop'), true);
								this._tutorialHand.position = new PIXI.Point(common.STAGE_WIDTH * 0.5, common.STAGE_HEIGHT * 0.5);
								this[_showHand]();
								break;
							case this.STATE.TOOL:
								this._tutorialHand.state.setAnimationByName(0, 'tap_' + (PIXI.utils.isMobile ? 'mobile' : 'desktop'), true);
								this._tutorialHand.position = common.game.hud.getBrushBtnPosition();
								this[_showHand]();
								break;
							case this.STATE.PALETTE:
								this._tutorialHand.state.setAnimationByName(0, 'tap_' + (PIXI.utils.isMobile ? 'mobile' : 'desktop'), true);
								this._tutorialHand.position = common.game.hud.getPaletteBtnPosition();
								this[_showHand]();
								break;
						}
					}
					if (!tutorialStep.isDone)
						break;
				}
				this._tutorialStepTimer = 0;
			}
		}
	}

	addEvent(tutorialEvent){
		this._arrEvent.push(tutorialEvent);
	}

	resize(){
		if (this._tutorialHand == null) return;

		if (this._state === this.STATE.DRAW)
			this._tutorialHand.position = new PIXI.Point(common.STAGE_WIDTH * 0.5, common.STAGE_HEIGHT * 0.5);
		else if (this._state === this.STATE.TOOL)
			this._tutorialHand.position = common.game.hud.getBrushBtnPosition();
		else if (this._state === this.STATE.PALETTE)
			this._tutorialHand.position = common.game.hud.getPaletteBtnPosition();
	}

	[_processEvents](){
		for (const tutorialEvent of this._arrEvent) {
			switch (tutorialEvent) {
				case commonGame.TUT_EVENT.CANVAS_VISIBLE:
					this._arrTutorialStep[this.STATE.DRAW].isValid = true;
					break;
				case commonGame.TUT_EVENT.PALETTE_SELECTED:
					if (this._state === this.STATE.DRAW
						|| this._state === this.STATE.TOOL)
						this[_hideHand]();
					else if (this._state === this.STATE.PALETTE) {
						this[_hideHand](this[_checkTutorialEnd]());
						this._state = this.STATE.IDLE;
					}
					if (common.savedData.isPaletteTutorial) {
						this._arrTutorialStep[this.STATE.PALETTE].isDone = true;
						common.savedData.isPaletteTutorial = false;
						common.savedData.save();
					}
					this._isPaused = true;
					this._tutorialStepTimer = 0;
					break;
				case commonGame.TUT_EVENT.MENU_PANEL_SHOWN:
					if (this._state !== this.STATE.IDLE)
						this[_hideHand]();
					this._isPaused = true;
					this._tutorialStepTimer = 0;
					break;
				case commonGame.TUT_EVENT.MENU_PANEL_HIDDEN:
				case commonGame.TUT_EVENT.PALETTE_PANEL_HIDDEN:
					if (this._state !== this.STATE.IDLE)
						this[_showHand]();
					this._isPaused = false;
					this._tutorialStepTimer = 0;
					break;
				case commonGame.TUT_EVENT.PALETTE_BUTTON_ENABLED:
					this._arrTutorialStep[this.STATE.PALETTE].isValid = true;
					if (this._state === this.STATE.PALETTE)
						this[_showHand]();
					break;
				case commonGame.TUT_EVENT.PALETTE_BUTTON_DISABLED:
					this._arrTutorialStep[this.STATE.PALETTE].isValid = false;
					if (this._state === this.STATE.PALETTE)
						this[_hideHand]();
					break;
				case commonGame.TUT_EVENT.TOOL_SELECTED:
					if (this._state === this.STATE.TOOL) {
						this[_hideHand](this[_checkTutorialEnd]());
						this._state = this.STATE.IDLE;
					}
					if (common.savedData.isToolTutorial) {
						this._arrTutorialStep[this.STATE.TOOL].isDone = true;
						common.savedData.isToolTutorial = false;
						common.savedData.save();
					}
					this._tutorialStepTimer = 0;
					break;
				case commonGame.TUT_EVENT.CANVAS_DRAWN:
					if (this._state === this.STATE.DRAW) {
						this[_hideHand](this[_checkTutorialEnd]());
						this._state = this.STATE.IDLE;
					}
					if (common.savedData.isDrawTutorial) {
						this._arrTutorialStep[this.STATE.DRAW].isDone = true;
						common.savedData.isDrawTutorial = false;
						common.savedData.save();
					}
					this._tutorialStepTimer = 0;
					break;
			}
		}

		this._arrEvent = [];
	}

	[_showHand](){
		new P3.utils.TWEEN.Tween(this._tutorialHand, this.tweens)
		    .to({
		        alpha: 1
		    }, 250)
		    .easing(P3.utils.TWEEN.Easing.Linear.None)
		    .start();
	}

	[_hideHand](onComplete){
		new P3.utils.TWEEN.Tween(this._tutorialHand, this.tweens)
			.to({
				alpha: 0
			}, 250)
			.easing(P3.utils.TWEEN.Easing.Linear.None)
			.onComplete(()=>{
				onComplete && onComplete.call();
			})
			.start();
	}

	[_checkTutorialEnd]() {
		if (!common.savedData.isDrawTutorial && !common.savedData.isToolTutorial && !common.savedData.isPaletteTutorial)
			this.signals.onCompleted.dispatch();
	}

	get tweens() {
		return this._tweens;
	}

	set tweens(value) {
		this._tweens = value;
	}
}